/**
 * Server address handling and "why can't I reach the API" diagnostics.
 *
 * Collect's own server is reached on this site's origin (base '' after
 * normalizeBaseUrl): /api/collect/* is proxied by the hosting (vercel.json
 * rewrite, Vite proxy in dev), so the browser makes no cross-origin request
 * and there is no CORS to get wrong. A self-hosted server (Settings →
 * Advanced) is cross-origin, and there a browser reports a CORS block and a
 * dead host the same way: fetch() rejects with "Failed to fetch". To tell them
 * apart we repeat the request once as a no-cors GET to the public
 * /api/collect/health: an opaque response means something answered, so the
 * CORS-mode request was refused by the browser. A second rejection means the
 * host did not answer at all (offline, DNS, wrong URL).
 *
 * No credentials or API key are sent with the probe.
 * Pure apart from the injectable fetch, so it can be unit-tested.
 */

export type FailureKind = 'cors' | 'unreachable';

export type ConnectionAudience = 'login' | 'settings';

export interface ErrorLike {
  code?: string;
  message?: string;
  status?: number;
}

/**
 * Trim, drop trailing slashes, add a scheme when it is missing: http:// for a
 * local backend (localhost, 127.0.0.1, [::1]), https:// otherwise.
 * '' stays '' (same-origin / dev proxy).
 */
export function normalizeBaseUrl(raw: string | null | undefined): string {
  const t = (raw || '').trim().replace(/\/+$/, '');
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith('/')) return t; // same-origin prefix behind a reverse proxy
  const local = /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?(\/|$)/i.test(t);
  return `${local ? 'http' : 'https'}://${t}`;
}

/** Host shown in messages for a self-hosted server ("hive.example.org"); the page host for same-origin. */
export function hostOf(base: string, pageOrigin: string): string {
  try {
    return new URL(base || '/', pageOrigin || 'http://localhost').host;
  } catch {
    return base || pageOrigin;
  }
}

const LOOPBACK_HOST = /^(localhost|127\.0\.0\.1|\[::1\])$/i;
const SAME_ORIGIN_PATH = /^(\/[A-Za-z0-9._~-]+)+$/;
const BAD_SERVER = 'Enter the server address as https://your-hive.example (http:// only for localhost).';

/**
 * Settings → Advanced: the address of a self-hosted server, or '' for Collect's
 * own server on this site. Accepted: an absolute https URL (http only for a
 * loopback host) without user info, query or fragment — CollectClient appends
 * /api/collect/… to it — or a plain same-origin path prefix. Control
 * characters and backslashes are refused (the URL parser rewrites them).
 */
export function checkServerUrl(raw: string | null | undefined): { ok: true; base: string } | { ok: false; message: string } {
  const t = (raw || '').trim();
  if (/[\u0000-\u001F\u007F\\]/.test(t)) return { ok: false, message: BAD_SERVER };
  // another scheme ("ftp://…") would otherwise get https:// put in front of it
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(t) && !/^https?:\/\//i.test(t)) return { ok: false, message: BAD_SERVER };
  const base = normalizeBaseUrl(t);
  if (!base) return { ok: true, base: '' };
  if (base.startsWith('/')) return SAME_ORIGIN_PATH.test(base) ? { ok: true, base } : { ok: false, message: BAD_SERVER };
  if (/[?#\s]/.test(base)) return { ok: false, message: BAD_SERVER };
  let u: URL;
  try {
    u = new URL(base);
  } catch {
    return { ok: false, message: BAD_SERVER };
  }
  if (u.username || u.password || u.pathname.includes('//')) return { ok: false, message: BAD_SERVER };
  if (u.protocol === 'https:' || (u.protocol === 'http:' && LOOPBACK_HOST.test(u.hostname))) return { ok: true, base };
  return { ok: false, message: BAD_SERVER };
}

export async function diagnoseFetchFailure(
  healthUrl: string,
  fetchImpl: (input: string, init?: RequestInit) => Promise<unknown> = (i, init) => fetch(i, init),
): Promise<FailureKind> {
  try {
    await fetchImpl(healthUrl, { mode: 'no-cors', cache: 'no-store', credentials: 'omit' });
    return 'cors';
  } catch {
    return 'unreachable';
  }
}

/**
 * fetch() rejected. `host` is the self-hosted server's host, or '' for Collect's
 * own server (same origin: no CORS involved, so only "unreachable" is told).
 */
export function failureMessage(kind: FailureKind, host: string, pageOrigin: string): string {
  if (!host) {
    return 'Collect could not reach its server. Check your internet connection and try again.';
  }
  if (kind === 'cors') {
    return `The server at ${host} answers, but your browser blocked the request: that server does not allow ${pageOrigin} (CORS). `
      + 'Its operator has to allow this site.';
  }
  // also what a CSP block looks like: the hosted site may only connect to itself (vercel.json)
  return `No answer from ${host}. Check the server address under Advanced and your connection, `
    + 'or this Collect site may not be allowed to reach that server.';
}

export const TOO_LARGE_MESSAGE = 'This capsule is too large to send from the browser. Send fewer files at a time, or send it with the Collect CLI.';

/**
 * A failed HTTP answer that carries no Collect error of its own (a proxy or
 * gateway page): a stable code and a plain message, or null to use the status.
 * 413 always gets the size message, whoever refused the body.
 */
export function statusFailure(status: number): { code: string; message: string } | null {
  if (status === 413) return { code: 'PAYLOAD_TOO_LARGE', message: TOO_LARGE_MESSAGE };
  if (status === 502 || status === 503 || status === 504) {
    return { code: 'UNAVAILABLE', message: `The Collect server did not answer (HTTP ${status}). Try again in a minute.` };
  }
  return null;
}

export function isAuthError(e: ErrorLike): boolean {
  return e.code === 'INVALID_API_KEY' || e.code === 'UNAUTHORIZED' || e.status === 401 || e.status === 403;
}

/** Human message for a failed connection test, worded for the login or the settings screen. */
export function describeConnectionError(e: ErrorLike, audience: ConnectionAudience): string {
  if (isAuthError(e)) {
    const why = e.code || `HTTP ${e.status}`;
    return audience === 'login'
      ? `The access key was not accepted (${why}). Check it, or ask your Qodoonect contact for a new one.`
      : `The server answers, but did not accept this access key (${why}). Check it, or ask your Qodoonect contact for a new one.`;
  }
  if (['CORS_BLOCKED', 'NETWORK', 'NOT_JSON', 'UNAVAILABLE', 'PAYLOAD_TOO_LARGE'].includes(e.code || '')) {
    return e.message || e.code || 'request failed';
  }
  return `${e.code || 'ERROR'}: ${e.message || 'request failed'}`;
}
